import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog.jsx';
import { ExternalLink } from 'lucide-react';
import { getLicensePdfBlob } from '@/services/licenses.service.js';

// Inline PDF viewer. Fetches the PDF with the broker JWT and shows it from an
// object URL (the COISheet pattern). It used to point <object> at the backend
// URL with no headers and lean on the CF Pages proxy injecting X-API-Key; an
// API key through the proxy authorises nothing since 2026-10-03.
export default function PdfViewerDialog({ open, onOpenChange, kind, id, title }) {
  const [src, setSrc] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !id) return undefined;
    let url = null;
    let cancelled = false;
    setSrc(null);
    setError(null);
    getLicensePdfBlob(kind, id)
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setSrc(url);
      })
      .catch((e) => { if (!cancelled) setError(e.message || 'PDF failed to load'); });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [open, kind, id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] w-[95vw] sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2 pr-6">
            <span className="truncate">{title || 'License PDF'}</span>
            {src && <a href={src} target="_blank" rel="noreferrer" className="flex shrink-0 items-center gap-1 text-xs text-accent hover:underline">Open <ExternalLink size={12} /></a>}
          </DialogTitle>
        </DialogHeader>
        {error && <p className="p-4 text-sm text-text-muted">{error}</p>}
        {!src && !error && <p className="p-4 text-sm text-text-muted">Loading the PDF...</p>}
        {src && <object data={src} type="application/pdf" className="h-[75vh] w-full rounded-card border border-border">
          <p className="p-4 text-sm text-text-muted">Can't preview inline. <a href={src} target="_blank" rel="noreferrer" className="text-accent underline">Open the PDF</a>.</p>
        </object>}
      </DialogContent>
    </Dialog>
  );
}
